import clsx from 'clsx'
import {
  type Dispatch,
  type FormEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react'

import { Icon } from '@fv/client-components'

import CheckboxField from '@/components/inputs/CheckboxField'
import { Pinline } from '@/components/Pinline'
import { ShipperTooltip } from '@/components/ShipperTooltip'

import { VendorShipmentIcon } from '../shipment-list/VendorShipmentIndicator'
import { AcceptedAuditForm } from './AcceptedAuditForm'
import ArchivedAuditForm from './ArchivedAuditForm'
import AuditCardHeader from './AuditCardHeader'
import { AuditPriceAlert } from './components/AuditPriceAlert'
import DisputedAuditForm from './DisputedAuditForm'
import OutstandingAuditForm from './OutstandingAuditForm'
import type { Audit } from './types'

type AuditListCardProps = {
  auditsToArchive: string[]
  data: Audit
  isActive: boolean
  isRemitting: boolean
  setActiveAudit: Dispatch<SetStateAction<string | null>>
  setAuditsToArchive: Dispatch<SetStateAction<string[]>>
  status: 'accepted' | 'archived' | 'disputed' | 'outstanding'
}

const AuditListCard = ({
  auditsToArchive,
  data,
  isActive,
  isRemitting,
  setActiveAudit,
  setAuditsToArchive,
  status,
}: AuditListCardProps) => {
  const { loadId, payments } = data
  const cardRef = useRef<HTMLDivElement | null>(null)
  const isAccepted = status === 'accepted'
  const autoAccepted = !!payments?.some(p => p.addedBy?.type === 'system')

  useEffect(() => {
    isActive && cardRef.current?.scrollIntoView({ block: 'nearest' })
  }, [isActive])

  const onCheckboxChange = useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      const { checked } = e.target as HTMLInputElement

      setAuditsToArchive(prev => {
        if (checked) return prev.concat(loadId)
        return prev.filter((id: string) => id !== loadId)
      })
    },
    [loadId, setAuditsToArchive],
  )

  return (
    <div
      className={clsx('audit-card', {
        'ml-10': isAccepted,
      })}
      ref={cardRef}
    >
      {isAccepted && (
        <div className="absolute -left-[2.5rem] bottom-0 top-0 flex w-[26px] flex-col border border-fv-green bg-fv-green-50">
          {isRemitting ? (
            <span className="flex !justify-center">
              <Icon icon="spinner" />
            </span>
          ) : (
            <>
              <CheckboxField
                className="-ml-px -mt-px"
                checked={auditsToArchive.includes(loadId)}
                name={`archive_${loadId}`}
                onChange={onCheckboxChange}
              />
              {autoAccepted && (
                <ShipperTooltip label="This invoice was automatically accepted">
                  <Icon icon="server" className="mt-2" />
                </ShipperTooltip>
              )}
            </>
          )}
        </div>
      )}

      <AuditCardHeader data={data} />
      {data.quote.modifiedAfterBooking && (
        <AuditPriceAlert quote={data.quote} />
      )}

      {data.isVendor && (
        <div className="absolute top-0 right-0">
          <ShipperTooltip label="This is a vendor shipment">
            <div className="border-fv-gray-300 border-b border-l p-2 bg-white">
              <VendorShipmentIcon />
            </div>
          </ShipperTooltip>
        </div>
      )}
      <Pinline className="mt-0 mb-4" />

      {isAccepted && (
        <AcceptedAuditForm
          audit={data}
          isActive={isActive}
          setActive={setActiveAudit}
        />
      )}

      {status === 'archived' && (
        <ArchivedAuditForm
          audit={data}
          isActive={isActive}
          setActive={setActiveAudit}
        />
      )}

      {status === 'disputed' && (
        <DisputedAuditForm
          audit={data}
          isActive={isActive}
          setActive={setActiveAudit}
        />
      )}

      {status === 'outstanding' && (
        <OutstandingAuditForm
          audit={data}
          isActive={isActive}
          setActive={setActiveAudit}
        />
      )}
    </div>
  )
}

export default AuditListCard
